/**
 * Writing a plan, with the arithmetic showing as you type.
 *
 * The numbers on the right — risk per share, position size, cost, R at each
 * target, blended planned R — are the reason `lib/plan-math.ts` is a client
 * module. A round trip per keystroke would make this feel broken, and computing
 * them twice is how the form and the journal end up disagreeing.
 *
 * ## The form is a draft until it is saved
 *
 * Every field is a **string** while it is being typed, and only parsed on save.
 * Holding numbers would make a half-typed `1.` either `1` or `NaN`, and both
 * fight the person entering it — the first by deleting the decimal point they
 * just pressed, the second by blanking the field.
 *
 * ## What it will not let you do
 *
 * Save with the stop on the wrong side of the entry. That is not a preference:
 * `direction` decides the sign of every R calculation, so a long whose stop sits
 * above its entry produces a negative risk-per-share and a position size that
 * is either infinite or zero. The form says so rather than saving a plan whose
 * numbers cannot mean anything.
 *
 * It also will not edit a filled plan's entry — the server refuses that, and
 * offering a control that produces an error is worse than not offering it.
 */

import { useMemo, useState } from "react";
import type { Plan } from "@qufolio/model";

import { savePlan } from "../api/journal";
import {
  plannedR,
  positionCost,
  positionSize,
  riskPerShare,
  targetR,
} from "../lib/plan-math";
import { mintUlid } from "@qufolio/model";
// The plan's currency follows its own symbol — the three hardcoded "$" here
// and in TradesView were the entire currency surface in the app, and each is
// wrong the moment a plan names an NSE symbol.
import { formatMoney, marketForSymbol } from "../lib/market";

export interface PlanFormProps {
  /** Pre-filled from the chart that opened it. */
  symbol: string;
  accountId: string;
  /** Present when editing rather than creating. */
  plan?: Plan;
  /**
   * The last traded price, when the chart knows it.
   *
   * Seeds a *new* plan only — entry at the price, a stop below it and one
   * target above, so the form opens with a complete, arithmetically valid plan
   * rather than four empty boxes. An empty form makes the user do the chart's
   * arithmetic by hand, and the numbers are already on screen.
   *
   * Never applied when editing: overwriting a saved entry with today's price
   * would silently rewrite the plan the user opened to read.
   */
  price?: number;
  /**
   * The chart's last bar, in unix seconds — where a **new** plan's levels start.
   *
   * A drawn plan takes this from the gesture. A typed one has no gesture, and
   * `createdAt` is a moment during the session while daily bars are stamped
   * midnight, so a plan without it starts past the end of the data and its
   * levels span the whole chart rather than raying from a point on it.
   *
   * Never applied when editing, for the same reason `price` is not: reopening a
   * saved plan would silently move where its levels begin.
   */
  startAt?: number;
  onSaved: (plan: Plan) => void;
  onCancel: () => void;
}

interface Draft {
  direction: "long" | "short";
  entry: string;
  entryOrder: "limit" | "stop" | "market";
  stop: string;
  risk: string;
  stopRule: string;
  timeLimitDays: string;
  thesis: string;
  targets: { id: string; price: string; share: string }[];
}

/**
 * How far a seeded stop sits from the entry, as a fraction.
 *
 * 3% is a starting point, not a recommendation — it is close enough to be
 * plausible on a daily chart and it is meant to be changed. The alternative,
 * seeding nothing, means the form opens with a stop of zero and a position size
 * of infinity, which is a worse first impression than a number to edit.
 */
const SEED_STOP = 0.03;

/** Target one at 2R, which is what makes the seeded plan worth taking at all. */
const SEED_TARGET_R = 2;

/** T2, further out — the runner the first exit pays for. */
const SEED_TARGET_2_R = 3;

function draftOf(plan: Plan | undefined, price?: number): Draft {
  if (!plan && price !== undefined && price > 0) {
    const entry = round2(price);
    const stop = round2(price * (1 - SEED_STOP));
    return {
      direction: "long",
      entry: String(entry),
      entryOrder: "limit",
      stop: String(stop),
      risk: "500",
      stopRule: "none",
      timeLimitDays: "5",
      thesis: "",
      // **Two targets, T1 and T2.** A single exit is an all-or-nothing plan,
      // and scaling out is the thing a target list is for — so the seeded plan
      // is one you could actually take. Equal shares, which
      // `normalise_shares` keeps summing to one on the server.
      //
      // T2 is the one the chart lets you delete (see `canRemoveTarget`): the
      // last is always the furthest out, so removing it leaves T1 meaning
      // exactly what it meant.
      targets: [
        {
          id: mintUlid(),
          price: String(round2(entry + (entry - stop) * SEED_TARGET_R)),
          share: "1",
        },
        {
          id: mintUlid(),
          price: String(round2(entry + (entry - stop) * SEED_TARGET_2_R)),
          share: "1",
        },
      ],
    };
  }
  return draftOfPlan(plan);
}

/** Prices carry cents. More decimals here would be arithmetic noise in a box. */
const round2 = (value: number): number => Math.round(value * 100) / 100;

function draftOfPlan(plan: Plan | undefined): Draft {
  return {
    direction: (plan?.direction as Draft["direction"]) ?? "long",
    entry: plan ? String(plan.entry) : "",
    entryOrder: (plan?.entryOrder as Draft["entryOrder"]) ?? "limit",
    stop: plan ? String(plan.stop) : "",
    risk: plan ? String(plan.risk) : "500",
    stopRule: plan?.stopRule ?? "none",
    timeLimitDays: plan?.timeLimitDays != null ? String(plan.timeLimitDays) : "5",
    thesis: plan?.thesis ?? "",
    targets: (plan?.targets ?? []).map((t) => ({
      id: t.id,
      price: String(t.price),
      share: String(t.share),
    })),
  };
}

const number = (text: string): number => {
  const value = Number(text);
  return Number.isFinite(value) ? value : 0;
};

/** The plan a draft describes, for the arithmetic to read while it is typed. */
function previewPlan(draft: Draft, symbol: string, accountId: string, plan?: Plan): Plan {
  return {
    id: plan?.id ?? "preview",
    accountId,
    symbol,
    direction: draft.direction,
    entry: number(draft.entry),
    entryOrder: draft.entryOrder,
    stop: number(draft.stop),
    risk: number(draft.risk),
    status: plan?.status ?? "draft",
    targets: draft.targets.map((t) => ({
      id: t.id,
      price: number(t.price),
      share: number(t.share),
    })),
    // Carried so R measures from the frozen stop on a plan that has filled —
    // the preview must agree with the list, or the same plan reads two ways.
    ...(plan?.planned ? { planned: plan.planned } : {}),
  } as Plan;
}

/**
 * Why this draft cannot be saved yet, or `null`.
 *
 * One message rather than per-field errors: a plan is four numbers that only
 * mean anything together, and "stop must be below entry for a long" is the
 * whole fault rather than a property of either field.
 */
function refusal(draft: Draft, preview: Plan): string | null {
  if (!draft.entry.trim() || !draft.stop.trim()) return "An entry and a stop are needed.";
  if (preview.entry <= 0 || preview.stop <= 0) return "Prices must be above zero.";
  if (preview.risk <= 0) return "Risk must be above zero.";
  if (draft.direction === "long" && preview.stop >= preview.entry) {
    return "A long's stop sits below its entry — otherwise there is no risk to size against.";
  }
  if (draft.direction === "short" && preview.stop <= preview.entry) {
    return "A short's stop sits above its entry — otherwise there is no risk to size against.";
  }
  if (positionSize(preview) < 1) {
    return "That risk buys no whole shares. Raise the risk or tighten the stop.";
  }
  return null;
}

export function PlanForm({
  symbol,
  accountId,
  plan,
  price,
  startAt,
  onSaved,
  onCancel,
}: PlanFormProps) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(plan, price));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preview = useMemo(
    () => previewPlan(draft, symbol, accountId, plan),
    [draft, symbol, accountId, plan],
  );
  const cannot = refusal(draft, preview);
  const frozen = plan?.planned != null;

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const addTarget = () =>
    setDraft((d) => ({
      ...d,
      // Shares are rescaled server-side, so an even split is a starting point
      // rather than a claim: two targets at 1 each arrive as 0.5 and 0.5.
      targets: [...d.targets, { id: mintUlid(), price: "", share: "1" }],
    }));

  const save = async () => {
    if (cannot) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await savePlan({
        ...preview,
        id: plan?.id ?? mintUlid(),
        status: plan?.status ?? "armed",
        // Creating only — see `startAt`. `plan?.validFrom` on the edit path so
        // an existing one is carried through rather than dropped.
        validFrom: plan
          ? plan.validFrom
          : startAt !== undefined
            ? new Date(startAt * 1000).toISOString()
            : undefined,
        stopRule: draft.stopRule === "none" ? undefined : (draft.stopRule as Plan["stopRule"]),
        timeLimitDays: draft.timeLimitDays ? Number(draft.timeLimitDays) : undefined,
        thesis: draft.thesis.trim() || undefined,
      });
      onSaved(saved);
    } catch (cause) {
      // Shown and left on screen, never cleared: a fill or a plan has no
      // successor to supersede it, so a form that reset itself on a failed save
      // would lose what was typed and look like it worked.
      setError(cause instanceof Error ? cause.message : "could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={PANEL} role="dialog" aria-label={`New plan for ${symbol}`}>
      <div style={HEAD}>
        <strong>{plan ? "Edit plan" : "New plan"}</strong>
        <span style={{ color: "var(--qf-text-muted)" }}>{symbol}</span>
      </div>

      <div style={BODY}>
        <div style={COLUMN}>
          <SelectField
            label="Direction"
            value={draft.direction}
            onChange={(value) => set("direction", value as Draft["direction"])}
          >
            <option value="long">Long</option>
            <option value="short">Short</option>
          </SelectField>

          <label style={FIELD}>
            Entry
            <input
              aria-label="Entry"
              inputMode="decimal"
              // A filled plan's entry is a fact. The server refuses to change
              // it, so the control says so rather than producing an error.
              disabled={frozen}
              title={frozen ? "A filled plan's entry cannot change — correct the fill" : undefined}
              value={draft.entry}
              onChange={(e) => set("entry", e.target.value)}
              style={INPUT}
            />
          </label>

          <SelectField
            label="Order"
            aria-label="Entry order"
            value={draft.entryOrder}
            onChange={(value) => set("entryOrder", value as Draft["entryOrder"])}
          >
            {/* A level dragged onto a chart is a limit by default: it is the
                order that cannot fill worse than the price you pointed at. */}
            <option value="limit">Limit</option>
            <option value="stop">Stop</option>
            <option value="market">Market</option>
          </SelectField>

          <label style={FIELD}>
            Stop
            <input
              aria-label="Stop"
              inputMode="decimal"
              value={draft.stop}
              onChange={(e) => set("stop", e.target.value)}
              style={INPUT}
            />
          </label>

          <label style={FIELD}>
            Risk ($)
            <input
              aria-label="Risk"
              inputMode="decimal"
              value={draft.risk}
              onChange={(e) => set("risk", e.target.value)}
              style={INPUT}
            />
          </label>

          <SelectField
            label="Stop rule"
            value={draft.stopRule}
            onChange={(value) => set("stopRule", value)}
          >
            <option value="none">Leave it</option>
            <option value="breakeven">To breakeven</option>
            <option value="half-r">To +0.5R</option>
            <option value="one-r">To +1R</option>
          </SelectField>
        </div>

        <div style={COLUMN}>
          <div style={{ ...FIELD, gap: 4 }}>
            <span>Targets</span>
            {draft.targets.map((target, index) => (
              <div key={target.id} style={{ display: "flex", gap: 4 }}>
                <input
                  aria-label={`Target ${index + 1} price`}
                  inputMode="decimal"
                  placeholder="price"
                  value={target.price}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      targets: d.targets.map((t, i) =>
                        i === index ? { ...t, price: e.target.value } : t,
                      ),
                    }))
                  }
                  style={{ ...INPUT, flex: 2 }}
                />
                <input
                  aria-label={`Target ${index + 1} share`}
                  inputMode="decimal"
                  placeholder="share"
                  value={target.share}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      targets: d.targets.map((t, i) =>
                        i === index ? { ...t, share: e.target.value } : t,
                      ),
                    }))
                  }
                  style={{ ...INPUT, flex: 1 }}
                />
                <button
                  type="button"
                  aria-label={`Remove target ${index + 1}`}
                  onClick={() =>
                    setDraft((d) => ({
                      ...d,
                      targets: d.targets.filter((_, i) => i !== index),
                    }))
                  }
                  style={SMALL}
                >
                  ×
                </button>
              </div>
            ))}
            <button type="button" onClick={addTarget} style={SMALL}>
              + target
            </button>
          </div>

          <label style={FIELD}>
            Thesis
            <textarea
              aria-label="Thesis"
              rows={3}
              value={draft.thesis}
              onChange={(e) => set("thesis", e.target.value)}
              style={{ ...INPUT, resize: "vertical" }}
            />
          </label>
        </div>

        {/* The arithmetic, live. This is why `plan-math` is a client module. */}
        <div style={{ ...COLUMN, minWidth: 190 }}>
          <Readout
            label="Risk per share"
            value={formatMoney(riskPerShare(preview), marketForSymbol(symbol))}
          />
          <Readout label="Position" value={`${positionSize(preview)} sh`} />
          <Readout
            label="Cost"
            value={formatMoney(positionCost(preview), marketForSymbol(symbol), { decimals: 0 })}
          />
          {targetR(preview).map((t, index) => (
            <Readout
              key={t.target.id}
              label={`T${index + 1}`}
              value={`${t.r >= 0 ? "+" : ""}${t.r.toFixed(2)}R`}
            />
          ))}
          {/* Blended, not the first target and not the furthest — a 50/50 at
              +2.00R and +3.93R is a 2.97R plan. */}
          <Readout
            label="Planned"
            value={`${plannedR(preview) >= 0 ? "+" : ""}${plannedR(preview).toFixed(2)}R`}
            strong
          />
        </div>
      </div>

      {(cannot || error) && (
        <div style={{ ...NOTE, color: error ? "var(--qf-negative)" : "var(--qf-text-muted)" }}>
          {error ?? cannot}
        </div>
      )}

      <div style={FOOT}>
        <button type="button" onClick={onCancel} style={BUTTON}>
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={cannot !== null || saving}
          style={{ ...BUTTON, fontWeight: 600 }}
        >
          {saving ? "Saving…" : plan ? "Save" : "Arm plan"}
        </button>
      </div>
    </div>
  );
}

function Readout({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
      <span style={{ color: "var(--qf-text-muted)" }}>{label}</span>
      <span style={{ fontWeight: strong ? 700 : 400, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </span>
    </div>
  );
}

const PANEL: React.CSSProperties = {
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  padding: 12,
  display: "flex",
  flexDirection: "column",
  gap: 10,
  minWidth: 620,
};
const HEAD: React.CSSProperties = { display: "flex", gap: 8, alignItems: "baseline" };
const BODY: React.CSSProperties = { display: "flex", gap: 16 };
const COLUMN: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 8, flex: 1 };
/**
 * A labelled dropdown that looks like one.
 *
 * The three selects here were styled exactly like the text inputs beside them
 * and read as static text — "Stop rule: Leave it" looked like a statement about
 * the plan rather than a control with four options behind it, and one of them
 * was reported as a missing feature.
 *
 * The native control paints its own arrow in the **OS** palette, which against
 * `--qf-surface-raised` on a dark theme is dark-on-dark and effectively
 * invisible. So the native arrow is suppressed and replaced by a glyph that
 * inherits the app's own `color` and therefore survives a theme switch.
 */
function SelectField({
  label,
  value,
  onChange,
  children,
  "aria-label": ariaLabel,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
  "aria-label"?: string;
}) {
  return (
    <label style={FIELD}>
      {label}
      <span style={{ position: "relative", display: "block" }}>
        <select
          aria-label={ariaLabel ?? label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={SELECT}
        >
          {children}
        </select>
        <span aria-hidden style={CHEVRON}>
          ▾
        </span>
      </span>
    </label>
  );
}

const FIELD: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  color: "var(--qf-text-secondary)",
};
const INPUT: React.CSSProperties = {
  background: "var(--qf-surface-raised)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  color: "var(--qf-text)",
  font: "inherit",
  padding: "3px 6px",
};
const SELECT: React.CSSProperties = {
  ...INPUT,
  // Fills its label the way the inputs beside it do. A select sizes to its
  // widest option by default, so without this the three would each be a
  // different width and the column would look ragged.
  width: "100%",
  cursor: "pointer",
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  // Room for the chevron, so a long option ("To breakeven") does not run under
  // it.
  paddingRight: 22,
};

const CHEVRON: React.CSSProperties = {
  position: "absolute",
  right: 7,
  top: "50%",
  transform: "translateY(-50%)",
  // The glyph must not eat the click that opens the menu it advertises.
  pointerEvents: "none",
  color: "var(--qf-text-secondary)",
  fontSize: 10,
};

const SMALL: React.CSSProperties = { ...INPUT, cursor: "pointer", padding: "2px 6px" };
const BUTTON: React.CSSProperties = { ...INPUT, cursor: "pointer", padding: "4px 12px" };
const NOTE: React.CSSProperties = { fontSize: 12 };
const FOOT: React.CSSProperties = { display: "flex", gap: 8, justifyContent: "flex-end" };
